/**
 * TrackPage — SPA hydration for /track/{slug} (Stage 9).
 *
 * The route is server-rendered by the Express proxy on direct hits (see
 * frontend/server.js and backend/music_ssr.py `render_track`). When React
 * hydrates or the user navigates client-side, this component fetches the
 * fresh data via /api/track/resolve/{slug}, transparently follows 301s
 * (canonical slug replace), and renders the interactive track page with
 * preview / buy / share / download / related-tracks flows.
 */
import { musicLine } from "@/lib/musicAttrs";
import { useCurrency } from "@/lib/CurrencyContext";
import { ChargedInAed } from "@/components/CurrencySelector";
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Loader2, Play, Pause, ShoppingCart, ShoppingBag, Download, Check } from "lucide-react";
import { useTranslation } from "react-i18next";
import { resilientGet, ssrBodyFor } from "@/lib/resilientGet";
import { PrerenderFallback } from "@/components/PrerenderFallback";
import { resolveCoverUrl, ASSETS } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { useCart } from "@/lib/CartContext";
import { useLocale, restoreBaselineHead } from "@/lib/LocaleContext";
import { localisedTo } from "@/lib/i18nRoutes";
import { toast } from "sonner";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import Breadcrumbs from "@/components/Breadcrumbs";
import VerifyEmailBanner from "@/components/VerifyEmailBanner";
import { TrackCard, useTrackActions } from "@/components/CatalogPrimitives";
import { fireSnapViewContent } from "@/components/TrackingLoader";
import { ShareButton } from "@/components/ShareModal";
import SecurePayments from "@/components/SecurePayments";
import TermsBlock from "@/components/TermsBlock";
import { formatTrackTitle } from "@/lib/trackTitle";

function injectMeta(name, value, attr = "name") {
  let el = document.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", value || "");
}

function injectJsonLd(id, obj) {
  const existing = document.getElementById(id);
  if (existing) existing.remove();
  const s = document.createElement("script");
  s.type = "application/ld+json";
  s.id = id;
  s.text = JSON.stringify(obj);
  document.head.appendChild(s);
}


export default function TrackPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation();
  const { lang } = useLocale();
  const cart = useCart();
  const [state, setState] = useState({ loading: true, error: null, data: null });

  const {
    playingId,
    buying,
    downloadingId,
    onPlayToggle,
    onAddToCart,
    onBuy,
    onDownload,
    AudioElement,
  } = useTrackActions();

  useEffect(() => {
    let cancelled = false;
    setState({ loading: true, error: null, data: null });
    (async () => {
      try {
        const { data } = await resilientGet(`/track/resolve/${encodeURIComponent(slug)}`);
        if (cancelled) return;
        // 301 alias — replace the URL so the address bar shows the canonical slug.
        if (data.redirected && data.canonical_slug && data.canonical_slug !== slug) {
          navigate(localisedTo(`/track/${data.canonical_slug}`, lang), { replace: true });
          return;
        }
        setState({ loading: false, error: null, data });
      } catch (err) {
        if (cancelled) return;
        const status = err?.response?.status;
        setState({
          loading: false,
          error: status === 404 ? "not_found" : "error",
          data: null,
        });
      }
    })();
    return () => { cancelled = true; };
  }, [slug, navigate, user?.id]);

  // ────────────────────────────────────────────────────────────────────
  // CLIENT-SIDE SEO MITIGATION ONLY — confirmed-404 handling.
  //
  // When the backend has positively confirmed the slug does not exist
  // (status 404), inject `robots=noindex,nofollow` and strip any
  // homepage-style canonical / OG metadata that may be lingering from
  // a previous SPA route. This does NOT change the Production edge
  // HTTP status (static-host returns 200 — that remains a documented
  // infrastructure limitation).
  //
  // Strictly gated: only after state.error === "not_found".
  // Never applied while loading, during generic errors, or on a valid
  // Track page.
  // ────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (state.loading || !state.error) return;
    // Generic error with prerendered content: the first-response head is already correct.
    if (state.error !== "not_found" && ssrBodyFor(window.location.pathname)) return;
    const setMeta = (name, content, attr = "name") => {
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (!el) { el = document.createElement("meta"); el.setAttribute(attr, name); document.head.appendChild(el); }
      el.setAttribute("content", content);
    };
    setMeta("robots", "noindex,nofollow");
    // Remove inherited canonical / OG / Twitter metadata.
    document.querySelectorAll('link[rel="canonical"]').forEach((n) => n.remove());
    document.querySelectorAll('link[rel="alternate"][hreflang]').forEach((n) => n.remove());
    const social = [...document.querySelectorAll('meta[property^="og:"], meta[name^="twitter:"]')];
    social.forEach((n) => n.remove());
    document.querySelectorAll('script[type="application/ld+json"][data-ld="track"]').forEach((n) => n.remove());
    document.title = state.error === "not_found" ? `${t("notFound.title")} | DJ SPY` : "Something went wrong | DJ SPY";
    setMeta("description", "The track you were looking for was not found.");
    return () => { restoreBaselineHead(); social.forEach((n) => document.head.appendChild(n)); };
  }, [state.loading, state.error, t]);

  // Inject SEO meta + JSON-LD when data is available. Cleanup on unmount so
  // trails don't leak between routes.
  useEffect(() => {
    const data = state.data;
    if (!data?.track) return;
    const t2 = data.track;
    // Same values the SSR emits (backend music_ssr.effective_track_seo / track_json_ld).
    const arPublished = lang === "ar" && t2.ar_indexable === true;
    const arDraftFallback = lang === "ar" && !arPublished;
    const displayTitle = (arPublished && t2.title_ar) || formatTrackTitle(t2) || t2.title;
    const canonicalSlug = data.canonical_slug || slug;
    const site = "https://djspyofficial.com";
    // Every URL is self-canonical; unpublished Arabic stays noindex,follow and is
    // never listed as an hreflang alternate.
    const canonical = lang === "ar"
      ? `${site}/ar/track/${canonicalSlug}`
      : `${site}/track/${canonicalSlug}`;
    const description = String(
      (arPublished ? t2.seo_description_ar_effective : t2.seo_description_effective)
      || t2.description || `${displayTitle} — DJ SPY ${t2.track_type || "remix"}`,
    ).slice(0, 300);
    const seoTitle = (arPublished ? t2.seo_title_ar_effective : t2.seo_title_effective) || `${displayTitle} — DJ SPY Official`;
    const ldDescription = arPublished ? description : (t2.about || description);
    const publicGenre = t2.display_genre || undefined;
    const cover = t2.cover_url
      ? (t2.cover_url.startsWith("http") ? t2.cover_url : `${site}${t2.cover_url}`)
      : `${site}/logo.png`;
    document.title = seoTitle;
    injectMeta("description", description);
    // Robots: unpublished Arabic → noindex,follow (per user rules). Published or English → normal index.
    injectMeta(
      "robots",
      arDraftFallback ? "noindex,follow" : "index,follow,max-image-preview:large",
    );
    injectMeta("og:type", "website", "property");
    injectMeta("og:title", seoTitle, "property");
    injectMeta("og:description", description, "property");
    injectMeta("og:url", canonical, "property");
    injectMeta("og:image", cover, "property");
    injectMeta("og:locale", lang === "ar" ? "ar_AE" : "en_US", "property");
    injectMeta("twitter:card", "summary_large_image");
    injectMeta("twitter:title", seoTitle);
    injectMeta("twitter:description", description);
    injectMeta("twitter:image", cover);

    let link = document.querySelector('link[rel="canonical"]');
    if (!link) {
      link = document.createElement("link");
      link.setAttribute("rel", "canonical");
      document.head.appendChild(link);
    }
    link.setAttribute("href", canonical);

    // hreflang alternates: only emit the /ar counterpart when Arabic is published.
    document.querySelectorAll('link[rel="alternate"][hreflang]').forEach((n) => n.remove());
    const enHref = document.createElement("link");
    enHref.setAttribute("rel", "alternate");
    enHref.setAttribute("hreflang", "en");
    enHref.setAttribute("href", `${site}/track/${canonicalSlug}`);
    document.head.appendChild(enHref);
    if (t2.ar_indexable) {
      const arHref = document.createElement("link");
      arHref.setAttribute("rel", "alternate");
      arHref.setAttribute("hreflang", "ar");
      arHref.setAttribute("href", `${site}/ar/track/${canonicalSlug}`);
      document.head.appendChild(arHref);
    }
    const xDef = document.createElement("link");
    xDef.setAttribute("rel", "alternate");
    xDef.setAttribute("hreflang", "x-default");
    xDef.setAttribute("href", `${site}/track/${canonicalSlug}`);
    document.head.appendChild(xDef);

    injectJsonLd("music-recording-jsonld", {
      "@context": "https://schema.org",
      "@type": "MusicRecording",
      name: displayTitle,
      url: canonical,
      image: cover,
      genre: publicGenre,
      description: ldDescription,
      isFamilyFriendly: true,
      inLanguage: lang === "ar" ? "ar" : "en",
      byArtist: { "@type": "MusicGroup", name: "DJ SPY", url: `${site}/` },
      publisher: { "@type": "Organization", name: "DJ SPY Official", url: `${site}/` },
      datePublished: (t2.created_at || "").slice(0, 10),
    });
    injectJsonLd("product-jsonld", {
      "@context": "https://schema.org",
      "@type": "Product",
      name: displayTitle,
      description: ldDescription,
      image: [cover],
      brand: { "@type": "Brand", name: "DJ SPY" },
      offers: {
        "@type": "Offer",
        url: canonical,
        priceCurrency: "AED",
        price: t2.is_free ? "0.00" : Number(t2.price || 0).toFixed(2),
        availability: "https://schema.org/InStock",
      },
    });
    // One BreadcrumbList only: <Breadcrumbs> emits the localised one (spa-breadcrumb-jsonld).
    document.getElementById("breadcrumb-jsonld")?.remove();
    return () => {
      // Best-effort cleanup so JSON-LD from this page doesn't leak into others.
      ["music-recording-jsonld", "product-jsonld", "breadcrumb-jsonld"].forEach((id) => {
        const el = document.getElementById(id);
        if (el) el.remove();
      });
      document.querySelectorAll('link[rel="alternate"][hreflang]').forEach((n) => n.remove());
    };
  }, [state.data, slug, lang]);

  // Snapchat VIEW_CONTENT — fire once per meaningful detail-page view
  // (Fix 12 · Part B).  Consent is enforced at the pixel-install layer:
  // when advertising consent is denied, `window.snaptr` is undefined and
  // the helper is a silent no-op.  A per-track ref-check would be brittle
  // in React StrictMode; we key on the track id in a small in-tab guard.
  useEffect(() => {
    const t2 = state.data?.track;
    if (!t2 || !t2.id) return;
    const key = `djspy_snap_vc_${t2.id}`;
    try {
      if (typeof window !== "undefined" && window.sessionStorage
          && window.sessionStorage.getItem(key)) return;
    } catch { /* proceed if sessionStorage denied */ }
    const ok = fireSnapViewContent({
      trackId: t2.id,
      trackType: t2.track_type,
      value: t2.is_free ? 0 : Number(t2.price || 0),
      currency: "AED",
    });
    if (ok) {
      try { window.sessionStorage.setItem(key, "1"); } catch { /* noop */ }
    }
  }, [state.data?.track?.id]);

  if (state.loading) {
    return (
      <div className="min-h-screen bg-black text-white">
        <Nav />
        <div className="pt-40 flex items-center justify-center text-white/50">
          <Loader2 size={20} className="animate-spin mr-3" /> {t("trackPage.loading")}
        </div>
      </div>
    );
  }

  if (state.error === "not_found") {
    return (
      <div className="min-h-screen bg-black text-white" data-testid="track-page-404">
        <Nav />
        <div className="pt-40 pb-24 px-4 max-w-2xl mx-auto text-center">
          <div className="font-mono text-xs tracking-[0.3em] text-red-brand">// 404</div>
          <h1 className="font-display text-5xl mt-3">{t("trackPage.notFoundTitle")}</h1>
          <p className="text-white/60 mt-4">
            {t("trackPage.notFoundBody")}
          </p>
          <Link
            to={localisedTo("/music", lang)}
            className="btn-primary inline-block mt-8"
            data-testid="track-404-back"
          >
            {t("trackPage.backToCatalogue")}
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  if (state.error) {
    const ssrEl = ssrBodyFor(window.location.pathname);
    if (ssrEl) return <PrerenderFallback el={ssrEl} testid="track-page-prerender-fallback" />;
    return (
      <div className="min-h-screen bg-black text-white" data-testid="track-page-error">
        <Nav />
        <div className="pt-40 pb-24 px-4 max-w-2xl mx-auto text-center">
          <h1 className="font-display text-3xl">{t("trackPage.somethingWentWrong")}</h1>
          <p className="text-white/60 mt-4">{t("trackPage.tryAgain")}</p>
        </div>
      </div>
    );
  }

  return (
    <TrackPageBody
      data={state.data}
      slug={slug}
      user={user}
      cart={cart}
      playingId={playingId}
      buying={buying}
      onPlayToggle={onPlayToggle}
      onAddToCart={onAddToCart}
      onBuy={onBuy}
      onOwned={() => setState((s) => ({ ...s, data: { ...s.data, track: { ...s.data.track, owned: true } } }))}
      onDownload={onDownload}
      downloadingId={downloadingId}
      AudioElement={AudioElement}
    />
  );
}

function TrackPageBody({
  data, slug, user, cart,
  playingId, buying, downloadingId, onPlayToggle, onAddToCart, onBuy, onOwned, onDownload, AudioElement,
}) {
  const { format: formatPrice } = useCurrency();
  const { t } = useTranslation();
  const { lang, isRTL } = useLocale();
  const track = data.track;
  const arPublished = lang === "ar" && track.ar_indexable === true;
  const arDraftFallback = lang === "ar" && !arPublished;
  const similar = data.similar_tracks || [];
  const displayTitle = (arPublished && track.title_ar) || formatTrackTitle(track) || track.title;
  const displayDescription = (arPublished && track.description_ar) || track.description || track.about;
  const canonicalSlug = data.canonical_slug || slug;
  const canonicalUrl = typeof window !== "undefined"
    ? `${window.location.origin}${localisedTo(`/track/${canonicalSlug}`, lang)}`
    : localisedTo(`/track/${canonicalSlug}`, lang);
  const genreCrumb = track.seo_genre_crumb;
  const publicGenre = track.display_genre || track.genre;
  const inCart = cart?.hasId?.(track.id);
  const isPlaying = playingId === track.id;

  const breadcrumbItems = useMemo(() => {
    const items = [
      { name: t("breadcrumbs.home"), path: localisedTo("/", lang) },
      { name: t("breadcrumbs.catalogue"), path: localisedTo("/music", lang) },
    ];
    if (genreCrumb) {
      items.push({ name: genreCrumb.name, path: localisedTo(genreCrumb.path, lang) });
    }
    items.push({ name: displayTitle });
    return items;
  }, [genreCrumb, displayTitle, lang, t]);

  const priceDisplay = track.is_free ? t("trackCard.free") : formatPrice(track.price);

  return (
    <div className="min-h-screen bg-black text-white" data-testid="track-page">
      <Nav />
      <AudioElement />
      <div className="pt-32 px-4 sm:px-6 max-w-6xl mx-auto">
        <VerifyEmailBanner />
        <Breadcrumbs items={breadcrumbItems} />
        {arDraftFallback && (
          <div
            data-testid="ar-coming-soon"
            className="mt-4 hairline rounded-xl border-yellow-500/30 bg-yellow-500/[0.06] px-4 py-2 text-[12px] font-mono text-yellow-200 tracking-wider"
          >
            {t("language.arabicComingSoon")}
          </div>
        )}
      </div>

      <section
        className="max-w-6xl mx-auto px-4 sm:px-6 pt-6 pb-16 grid gap-10 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)] items-center"
        data-testid="track-hero"
      >
        {/* Cover art with play overlay */}
        <div className="relative aspect-square rounded-2xl overflow-hidden bg-gradient-to-br from-[#181818] via-[#0d0d0d] to-[#1c0808] border border-white/10">
          <img
            src={resolveCoverUrl(track.cover_url)}
            onError={(e) => { e.currentTarget.src = ASSETS.trackFallbackCover; }}
            alt={`${displayTitle} — ${publicGenre || "DJ SPY"} cover art`}
            className="w-full h-full object-cover"
          />
          <button
            onClick={() => onPlayToggle(track)}
            data-testid="track-page-play"
            className={`absolute bottom-5 ${isRTL ? "left-5" : "right-5"} w-16 h-16 rounded-full flex items-center justify-center transition-transform hover:scale-105 ${
              isPlaying ? "bg-red-brand pulse-red" : "bg-white"
            }`}
            aria-label={isPlaying ? t("trackCard.pause") : t("trackCard.play")}
          >
            {isPlaying ? (
              <Pause size={26} className="text-white" />
            ) : (
              <Play size={26} className="text-black translate-x-0.5" />
            )}
          </button>
        </div>

        {/* Meta */}
        <div>
          <div className="font-mono text-[10px] tracking-[0.3em] text-red-brand">{t("trackPage.kicker")}</div>
          <h1
            className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[0.95] mt-3 tracking-tight break-words [overflow-wrap:anywhere]"
            data-testid="track-page-title"
          >
            {displayTitle}
          </h1>
          {track.artist && track.track_type !== "remix" && (
            <p className="mt-3 text-white/60" data-testid="track-page-artist" dir="ltr">{track.artist}</p>
          )}
          <div className="mt-4 flex flex-wrap gap-2">
            {genreCrumb && (
              <Link
                to={localisedTo(genreCrumb.path, lang)}
                className="px-3 py-1 text-[10px] font-mono tracking-[0.25em] uppercase rounded-full bg-red-brand/15 text-red-brand hover:bg-red-brand/25 transition-colors"
                data-testid="track-page-genre-link"
              >
                {publicGenre}
              </Link>
            )}
            <span
              className="px-3 py-1 text-[10px] font-mono tracking-[0.25em] uppercase rounded-full bg-white/5 text-white/70"
              data-testid="track-page-type"
            >
              {track.track_type || "remix"}
            </span>
            {musicLine(track) && (
              <span className="px-3 py-1 text-[10px] font-mono tracking-[0.25em] rounded-full bg-white/5 text-white/80" dir="ltr"
                data-testid="track-page-bpm-key">{musicLine(track)}</span>
            )}
          </div>
          <div
            className="mt-8 font-anton text-3xl text-red-brand"
            data-testid="track-page-price"
            dir="ltr"
          >
            {priceDisplay}
          </div>
          {!track.is_free && <ChargedInAed aedTotal={track.price} testid="track-page-charged-in-aed" className="mt-1" />}

          {/* Terms — required for checkout; hidden once the customer
              owns the track. Free "Get" flows also agree to T&Cs. */}
          {!track.owned && (
            <div className="mt-6">
              <TermsBlock testId="track-page-terms-checkbox" />
            </div>
          )}

          {/* CTA row */}
          <div className="mt-6 flex flex-wrap items-center gap-3">
            {track.owned ? (
              <button
                onClick={() => onDownload(track)}
                disabled={downloadingId === track.id}
                data-testid="track-page-download"
                className="btn-primary disabled:opacity-60 disabled:cursor-wait"
              >
                <Download size={16} /> {downloadingId === track.id ? t("library.preparing") : t("trackPage.downloadFull")}
              </button>
            ) : (
              <>
                <button
                  onClick={() => onBuy(track, { onDone: onOwned })}
                  disabled={buying === track.id}
                  data-testid="track-page-buy"
                  className="btn-primary"
                >
                  {buying === track.id ? (
                    <Loader2 size={16} className="animate-spin" />
                  ) : (
                    <ShoppingCart size={16} />
                  )}
                  {track.is_free ? t("trackPage.getItFree") : t("trackPage.buyNow")}
                </button>
                {!track.is_free && (
                  <button
                    onClick={() => onAddToCart(track)}
                    disabled={inCart}
                    data-testid="track-page-add-cart"
                    className="btn-ghost"
                  >
                    {inCart ? <Check size={16} className="text-red-brand" /> : <ShoppingBag size={16} />}
                    {inCart ? t("trackCard.inCart") : t("trackCard.addToCart")}
                  </button>
                )}
              </>
            )}
            <ShareButton
              url={canonicalUrl}
              title={displayTitle}
              description={displayDescription || `${displayTitle} — DJ SPY`}
              variant="ghost"
              size="md"
              testid="track-page-share"
              label={t("trackPage.share")}
            />
          </div>

          {/* Secure Payments strip — reinforces buyer trust directly under the Buy Now button. */}
          {!track.is_free && (
            <div className="mt-5" data-testid="track-page-secure-payments">
              <SecurePayments variant="compact" />
            </div>
          )}

          {/* Read the story chip */}
          {track.blog_slug && (
            <Link
              to={localisedTo(`/blog/${track.blog_slug}`, lang)}
              data-testid="track-page-story"
              className="inline-flex items-center gap-2 mt-8 text-[11px] font-mono tracking-[0.2em] uppercase text-white/55 hover:text-red-brand transition-colors border-t border-white/10 pt-5"
            >
              {t("trackPage.readStory", { title: displayTitle })} <ArrowRight size={12} />
            </Link>
          )}
        </div>
      </section>

      {/* About this Release — always visible, unique text per track. */}
      {(track.about || track.description || displayDescription) && (
        <section
          className="max-w-3xl mx-auto px-4 sm:px-6 pb-4 pt-4"
          data-testid="track-page-about"
          data-source={track.description ? "admin" : "auto"}
        >
          <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-6 sm:p-8">
            <div className="font-mono text-[10px] tracking-[0.3em] text-red-brand">
              {t("trackPage.aboutKicker")}
            </div>
            <h2
              className="font-display text-2xl sm:text-3xl mt-2 leading-tight"
              data-testid="track-page-about-heading"
            >
              {t("trackPage.aboutHeading", { title: displayTitle })}
            </h2>
            <p
              className="mt-4 text-white/80 text-base leading-relaxed"
              data-testid="track-page-about-body"
            >
              {displayDescription || track.about || track.description}
            </p>
          </div>
        </section>
      )}

      {/* Related tracks */}
      {similar.length > 0 && (
        <section
          className="max-w-6xl mx-auto px-4 sm:px-6 pb-24"
          data-testid="track-page-related"
        >
          <div className="flex items-baseline justify-between flex-wrap gap-2 mb-6">
            <div>
              <div className="font-mono text-[10px] tracking-[0.3em] text-red-brand">
                {t("trackPage.moreInGenre", { genre: (publicGenre || t("music.title")).toUpperCase() })}
              </div>
              <h2 className="font-display text-3xl sm:text-4xl mt-2">{t("trackPage.relatedTitle")}</h2>
            </div>
            {genreCrumb && (
              <Link
                to={localisedTo(genreCrumb.path, lang)}
                data-testid="track-page-more-genre"
                className="font-mono text-[10px] tracking-[0.2em] uppercase text-red-brand hover:underline"
              >
                {t("trackPage.browseAllGenre", { genre: genreCrumb.name })}
              </Link>
            )}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3.5" data-testid="track-page-related-grid">
            {similar.map((sim, idx) => (
              <TrackCard
                key={sim.id}
                track={sim}
                index={idx}
                playingId={playingId}
                onPlayToggle={onPlayToggle}
                onBuy={(tr) => onBuy(tr)}
                onDownload={onDownload}
                onAddToCart={onAddToCart}
                buying={buying}
                downloadingId={downloadingId}
                inCart={cart?.hasId?.(sim.id)}
              />
            ))}
          </div>
        </section>
      )}
      <Footer />
    </div>
  );
}
